import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/** A sheet's width in CSS pixels where the browser has not said yet how wide it is. */
const DEFAULT_WIDTH = 640;

/** What a box that is so many PDF points wide is sized by. */
export interface PointScale<E extends HTMLElement> {
  /** For the box whose width is measured. */
  ref: RefObject<E | null>;
  /** Sets `--pt` on that box: the pixels one point takes there, which `pt()` sizes everything inside it by. */
  style: CSSProperties;
  /** The same as a number, for a measurement made in code. */
  pixels: number;
}

/**
 * The scale of a box that stands for `points` PDF points of width: a sheet for the 595 of an
 * A4 page. Measured before the first paint, so a sheet is first drawn at the width its
 * column gives it and not at a size it then jumps away from, and again whenever the box is
 * resized.
 *
 * What is measured is the box from edge to edge, the first time and every time after it. The
 * results' place is one box in two shapes: a sheet, or a dashed place with a border and a
 * padding of its own for a design that prints no results. Measured inside those, the sheet
 * it turned back into was drawn at the narrower width for a frame.
 */
export const usePointScale = <E extends HTMLElement = HTMLDivElement>(points: number): PointScale<E> => {
  const ref = useRef<E>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => setWidth(element.getBoundingClientRect().width);

    measure();

    const resized = new ResizeObserver(measure);

    resized.observe(element);

    return () => resized.disconnect();
  }, []);

  const pixels = (width || DEFAULT_WIDTH) / points;

  return { ref, style: { '--pt': `${pixels}px` } as CSSProperties, pixels };
};
